'use client'

import { twMerge } from '@/lib/classname/twMerge'
import type React from 'react'
import type { ElementType, ReactElement } from 'react'
import sanitizeHtml from 'sanitize-html'
import { SANITIZE_HTML_OPTIONS } from '@/lib/utils/text-utils'

type BaseProps = {
  html: string
  /** If true, sanitize with minimal client-side logic. */
  sanitize?: boolean
  /** Optional post-processing step after sanitization (e.g., tweak links). */
  transformHtml?: (sanitizedHtml: string) => string
  className?: string
}

type PolymorphicProps<T extends ElementType> = {
  as?: T
} & StrictOmit<React.ComponentPropsWithoutRef<T>, 'children' | 'dangerouslySetInnerHTML'>

export type InnerHtmlProps<T extends ElementType = 'div'> = BaseProps &
  PolymorphicProps<T> &
  WithPayloadBlockId

export function InnerHtml<T extends ElementType = 'div'>({
  html,
  as,
  sanitize = true,
  transformHtml,
  className,
  'data-payload-block-id': dataPayloadBlockId,
  ...props
}: InnerHtmlProps<T>): ReactElement | null {
  const Component = (as ?? 'div') as ElementType

  const sanitized = sanitize ? sanitizeHtml(html, SANITIZE_HTML_OPTIONS) : html
  const finalHtml = transformHtml ? transformHtml(sanitized) : sanitized

  if (!finalHtml) {
    return null
  }

  return (
    <Component
      className={twMerge(className, 'prose-text')}
      dangerouslySetInnerHTML={{ __html: finalHtml }}
      data-payload-block-id={dataPayloadBlockId}
      {...props}
    />
  )
}

InnerHtml.displayName = 'InnerHtml'
